import React, { useState, useEffect, useRef } from "react";
import MenuHeader from "./MenuHeader";
import Modal from "react-modal";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import Swal from "sweetalert2";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { endpoints } from "../utils/utils";
import useApi from "../hooks/useApi";
import ZoneService from "../services/zone.service";
import CalendarService from "../services/calendar.service";
import UserService from "../services/user.service";
import UnavailableZoneService from "../services/unavailable_zones.service";
import { useLocation, useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import GeneralSettingService from "../services/general_setting.service";
import { asTitle } from "../shared/utils/string";
import useAuth from "../hooks/useAuth";
import { useNotifications } from "../context/NotificationProvider";
import { useTranslation } from 'react-i18next';

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    height: "40rem",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    overflow: "hidden auto",
    scrollbarWidth: "none",
  },
};

const Management = () => {
  const { t } = useTranslation('manager');
  Modal.setAppElement("#root");

  const handleClick = () => {
    const panels = document.querySelectorAll('.react-tabs__tab-panel--selected');
    const tabs = document.querySelectorAll('.react-tabs__tab--selected');

    panels.forEach(panel => panel.classList.remove('react-tabs__tab-panel--selected'));
    tabs.forEach(tab => tab.classList.remove('react-tabs__tab--selected'));
  };

  const [modalIsOpen, setIsOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [zones, setZones] = useState([]);
  const [unavailablezones, setunavailablezones] = useState([]);
  const [generalSettings, setGeneralSettings] = useState([]);
  const openModal = () => setIsOpen(true);
  const api = useApi();
  const [newZone, setNewZone] = useState({
    id: null,
    name: "",
    zip_code: "",
    is_active: true,
  });
  const [userData, setUserData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [initialUserData, setInitialUserData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
  });

  const [generalSettingData, setGeneralSettingData] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [userId, setUserId] = useState(null);
  const [hotelId, sethotelId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const [tabIndex, setTabIndex] = useState(0);
  const [userRoles, setUserRoles] = useState([]);
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);
  const [availabilities, setAvailabilities] = useState({});
  const [selectedDay, setSelectedDay] = useState(null);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [editIndex, setEditIndex] = useState(null);
  const { auth } = useAuth();
  const notify = useNotifications();

  // Fetch the logged-in user's profile
  const fetchUserProfile = async () => {
    try {
      const response = await UserService.currentUser({ with: 'roles' }, api);
      const user = response.user;
      const user_role = user.roles;
      setUserRoles(user_role);
      setUserId(user.id)
      setInitialUserData({
        firstname: user.firstname || "",
        lastname: user.lastname || "",
        email: user.email || "",
        phone: user.phone || "",
      });
      setUserData({
        firstname: user.firstname || "",
        lastname: user.lastname || "",
        email: user.email || "",
        phone: user.phone || "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  // Check if form data is modified
  const isFormModified = () => {
    return (
      userData.firstname !== initialUserData.firstname ||
      userData.lastname !== initialUserData.lastname ||
      userData.email !== initialUserData.email ||
      userData.phone !== initialUserData.phone
    );
  };

  const fetchAvailabilities = async (day) => {
    try {
      const response = await CalendarService.index(
        { hotel_id: hotelId, day_of_week: day },
        api
      );

      // Ensure & check the correct day with the filter
      const correctDayAvailabilities = Array.isArray(response)
        ? response.filter(item => item.day_of_week === day)
        : [];

      const flattenedAvailabilities = correctDayAvailabilities
        .map((item) => ({
          id: item.id,
          day_of_week: item.day_of_week,
          hotel_id: item.hotel_id,
          start_time: item.availability?.[0]?.start_time,
          end_time: item.availability?.[0]?.end_time,
        }))
        .filter((availability) => availability.start_time)
        .sort((a, b) => a.start_time.localeCompare(b.start_time));

      setAvailabilities((prev) => ({
        ...prev,
        [day]: flattenedAvailabilities,
      }));
    } catch (error) {
      setAvailabilities((prev) => ({
        ...prev,
        [day]: [],
      }));
    }
  };

  const openAvailabilityModal = async (day) => {
    setSelectedDay(day);
    setAvailabilityModalOpen(true);

    await fetchAvailabilities(day);
  };

  const closeAvailabilityModal = () => {
    setAvailabilityModalOpen(false);
    setStartTime("");
    setEndTime("");
    setEditIndex(null);
  };

  const addAvailability = async () => {
    if (!startTime || !endTime) {
      notify.warn("Please select a valid date range");
      return;
    }

    const newAvailability = { start_time: startTime, end_time: endTime };
    const payload = {
      day_of_week: selectedDay,
      hotel_id: auth?.user?.hotel?.[0]?.id,
      availability: [newAvailability],
    };


    try {
      if (editIndex !== null) {
        const updatedAvailability = availabilities[selectedDay][editIndex];
        await CalendarService.update(updatedAvailability.id, payload, api);
      } else {
        await CalendarService.store(payload, api);
      }

      await fetchAvailabilities(selectedDay);
      setStartTime("");
      setEndTime("");
      setEditIndex(null);
    } catch (error) {

    }
  };

  const deleteAvailability = async (index) => {
    const availabilityToDelete = availabilities[selectedDay][index];
    try {
      await CalendarService.delete(availabilityToDelete.id, api);

      setAvailabilities((prev) => ({
        ...prev,
        [selectedDay]: prev[selectedDay].filter((_, i) => i !== index),
      }));
    } catch (error) {

    }
  };

  const editAvailability = (index) => {
    const availabilityToEdit = availabilities[selectedDay][index];
    setStartTime(availabilityToEdit.start_time);
    setEndTime(availabilityToEdit.end_time);
    setEditIndex(index);
  };

  useEffect(() => {
    if (auth?.user?.hotel?.length > 0) {
      sethotelId(auth.user.hotel[0].id);
    }
  }, [auth]);

  const handleEditProfileClick = () => {
    setIsEditingProfile(true);
    setIsEditingPassword(false);
  };

  // Handle cancel action
  const handleCancelEdit = () => {
    setUserData(initialUserData);
    setIsEditingProfile(false);
    isFormModified(false);
  };

  // Update user's profile information
  const updateProfile = async (e) => {
    e.preventDefault();
    try {
      const updatedUser = { ...userData };
      await UserService.update(userId, updatedUser, api);
      setInitialUserData({ ...userData });
      setIsEditingProfile(false);
      isFormModified(false);
    } catch (error) {

    }
  };

  const handleEditPasswordClick = () => {
    setIsEditingPassword(true);
    setIsEditingProfile(false);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/verify-password', {
        password: userData.currentPassword,
        client_id: userId
      }, {
        headers: { "Content-Type": "application/json" },
      });
      console.log("dd", response)

      if (!response.data.success) {
        notify.warn('Le mot de passe actuel est incorrect.');
        return;
      }
    } catch (error) {
      notify.error('Une erreur est survenue lors de la vérification du mot de passe.');
      return;
    }

    if (userData.newPassword !== userData.confirmPassword) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "New passwords do not match!",
      });
      return;
    }

    const passwordRules = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRules.test(userData.newPassword)) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Password must be at least 8 characters long and include at least one uppercase letter, one number, and one special character.",
      });
      return;
    }

    try {
      // const response = await api.post(endpoints.UpdateUserPassword, {
      //   currentPassword: userData.currentPassword,
      //   newPassword: userData.newPassword,
      //   confirmPassword: userData.confirmPassword,
      // });
      const response = await UserService.update(userId, { password: userData.newPassword }, api);

      if (response.data.status === false) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: response.data.response,
        });
        return;
      }

      Swal.fire({
        icon: "success",
        title: "Password updated successfully!",
        showConfirmButton: false,
        timer: 1500,
      });

      // Reset only the password-related fields, keeping profile data intact
      setUserData((prevData) => ({
        ...prevData,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));

      setIsEditingPassword(false);
    } catch (error) {
      console.error("Error updating password:", error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: error.response?.data?.message || "An error occurred!",
      });
    }
  };

  const handleTimeDecrement = (event) => {
    const id = event.target.id.split('decrement-')[1];
    setGeneralSettingData(prevSettings =>
      prevSettings.map(setting =>
        setting.id == id && setting.value > 0
          ? {
            ...setting,
            value: setting.setting === 'surcharge' ? setting.value - 1 : setting.setting === 'VTA' ? setting.value - 0.1 : setting.value - 5,
            updated: true
          }
          : setting
      )
    );
  };

  const handleTimeIncrement = (event) => {
    const id = event.target.id.split('increment-')[1];
    setGeneralSettingData(prevSettings =>
      prevSettings.map(setting =>
        setting.id == id
          ? {
            ...setting,
            value: setting.setting === 'surcharge' ? setting.value + 1 : setting.setting === 'VTA' ? setting.value + 0.1 : setting.value + 5,
            updated: true
          }
          : setting
      )
    );
  };

  const handleCancelSettingsEdit = () => {
    setGeneralSettingData(generalSettings.map(general_setting => {
      return {
        id: general_setting.id,
        setting: general_setting.setting,
        value: Number(general_setting.value),
        updated: false
      }
    }));
  }

  const updateSettings = (e) => {
    e.preventDefault();
    generalSettingData.forEach(setting => {
      if (setting.updated) {
        try {
          GeneralSettingService.update(setting.id, setting, api);
          setting.updated = false;
        } catch (e) {

        }
      }
    });
  }
  // Organize zones into hierarchy based on zip code ranges
  const organizeZonesHierarchy = (flatZones) => {
    const processedZones = flatZones.map(zone => {
      const zipRanges = parseZipRanges(zone.zip_code);
      return {
        ...zone,
        ranges: zipRanges,
        children: []
      };
    });

    return processedZones.filter(zone => {
      let isChild = false;
      processedZones.forEach(potentialParent => {
        if (zone.id !== potentialParent.id && isZipInRange(zone.ranges, potentialParent.ranges)) {
          potentialParent.children.push(zone);
          isChild = true;
        }
      });
      return !isChild;
    });
  };

  // Parse zip code string into ranges
  const parseZipRanges = (zipString) => {
    const ranges = [];
    const parts = zipString.split(',').map(p => p.trim());

    parts.forEach(part => {
      if (part.includes('-')) {
        const [start, end] = part.split('-').map(Number);
        ranges.push({ start, end });
      } else {
        const num = Number(part);
        ranges.push({ start: num, end: num });
      }
    });
    return ranges;
  };

  // Check if one range falls within another
  const isZipInRange = (childRanges, parentRanges) => {
    return childRanges.every(childRange =>
      parentRanges.some(parentRange =>
        childRange.start >= parentRange.start && childRange.end <= parentRange.end
      )
    );
  };

  const fetchZones = async () => {

    try {
      const response = await ZoneService.index(null, api);
      let zonesData = [];
      if (response) {
        zonesData = response;
      } else {
        console.log("Unexpected response structure:", response);
        zonesData = [];
      }

      const sortedZones = zonesData.sort((a, b) => a.name?.localeCompare(b?.name));
      const organizedZones = organizeZonesHierarchy(sortedZones);
      setZones(organizedZones);
    } catch (error) {
      console.error("Error fetching zones:", error);
      console.error("Full error object:", JSON.stringify(error, null, 2));
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: error.response?.data?.message || "An error occurred!",
      });
    } finally {

    }
  };
  const fetchUnavailableZones = async () => {

    try {
      const response = await UnavailableZoneService.index(null, api);
      let unavailablezonesData = [];
      console.log("unvai data", response)
      if (response) {
        unavailablezonesData = response;
        setunavailablezones(unavailablezonesData);
      }
    } catch (error) {
      console.error("Error fetching unavailablezones:", error);
      console.error("Full error object:", JSON.stringify(error, null, 2));
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: error.response?.data?.message || "An error occurred!",
      });
    } finally {

    }
  };
  const fetchGeneralSettings = async () => {

    try {
      const response = await GeneralSettingService.index('setting=in:buffer_before,buffer_after,surcharge,VTA,first_buffer_travel_gap', api);
      setGeneralSettings(response);
      setGeneralSettingData(response.map(general_setting => {
        return {
          id: general_setting.id,
          setting: general_setting.setting,
          value: Number(general_setting.value),
          updated: false
        }
      }));
    } catch (error) {

    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab === "zone-management") {
      setTabIndex(1);
    } else if (tab === "unavailable-zones") {
      setTabIndex(2);
    } else if (tab === "hotel-availability") {
      setTabIndex(1);
    } else if (tab === "general-settings") {
      setTabIndex(3);
    } else {
      setTabIndex(0);
    }

    fetchGeneralSettings();
    fetchZones();
    fetchUnavailableZones();
    fetchUserProfile();
  }, [location]);

  const closeModal = () => {
    setIsOpen(false);
    setNewZone({
      id: null,
      name: "",
      zip_code: "",
      is_active: true,
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewZone((prevZone) => ({
      ...prevZone,
      [name]: type === "checkbox" ? checked : value,
    }));

    setUserData((prevUserData) => ({
      ...prevUserData,
      [name]: value,
    }));
  };

  // Validate ZIP code format
  const validateZipFormat = (zipString) => {
    const cleaned = zipString.replace(/\s/g, '');
    const parts = cleaned.split(',');

    for (const part of parts) {
      if (part.includes('-')) {
        const [start, end] = part.split('-');
        if (!start || !end || isNaN(start) || isNaN(end) ||
          parseInt(start) >= parseInt(end)) {
          return false;
        }
      } else {
        if (isNaN(part)) {
          return false;
        }
      }
    }
    return true;
  };

  // Create zone with API integration
  const createZone = async (e) => {
    e.preventDefault();

    if (!validateZipFormat(newZone.zip_code)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid ZIP Code Format',
        text: 'Please use correct format: Use comma (,) for specific ZIP codes and hyphen (-) for ranges. Example: 1000,1019,1020-1025',
      });
      return;
    }

    try {
      if (newZone.id) {
        await ZoneService.update(newZone.id, { name: newZone.name, zip_code: newZone.zip_code, is_active: newZone.is_active }, api);
      } else {
        await ZoneService.store(newZone, api);
      }

      fetchZones();
      closeModal();
    } catch (error) {

    }
  };

  const RenderZone = ({ zone }) => {
    const [isOpen, setIsOpen] = useState(false);
    const isCommaSeparated = zone.zip_code.includes(',');
    const isSingleZip = !zone.zip_code.includes(',') && !zone.zip_code.includes('-');
    const zip_codeClass = isSingleZip ? "single" : isCommaSeparated ? "multi" : "range";
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const handleMouseUp = () => setIsDragging(false);
    const handleMouseLeave = () => setIsDragging(false);
    const timeoutRef = useRef(null);
    const [editZoneId, setEditZoneId] = useState(null);


    const renderZipCodes = () => {
      const zipCodes = zone.zip_code.split(',').map((zip) => zip.trim());
      return zipCodes.join(', '); // Concatenate with commas for inline display
    };

    const toggleChildZones = () => {
      setIsOpen((prevState) => !prevState);
    };

    const handleStatusChange = (id, currentStatus) => {
      toggleZoneStatus(id, currentStatus);
    };

    const handleMouseDown = (e) => {
      const container = e.currentTarget;
      setIsDragging(true);
      setStartX(e.pageX - container.offsetLeft); // Store initial mouse position
      setScrollLeft(container.scrollLeft); // Store initial scroll position
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return; // Exit if not dragging
      e.preventDefault();
      const container = e.currentTarget;
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX); // Distance moved
      container.scrollLeft = scrollLeft - walk; // Update scroll position
    };


    const handleEditMouseLeave = () => {
      timeoutRef.current = setTimeout(() => {
        setSelectedZone(null);
        setEditZoneId(null)
      }, 250);
    };

    const handleEditMouseEnter = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };

    // Toggles the edit dropdown for a specific zone
    const toggleEditDropdown = (e, zoneId) => {
      setEditZoneId((prevId) => (prevId === zoneId ? null : zoneId));
    };

    const handleDelete = async (zoneId) => {
      try {
        const response = await ZoneService.forceDelete(zoneId, api);
        fetchZones();
      } catch (error) {

      }
    }

    return (
      <>
        <div className="list-details">
          <div className="lists">
            <div className="header-list">
              <div className="name">{zone.name}</div>

              <div
                className={`zip-code ${isOpen ? "open" : ""} ${zip_codeClass}`}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave} // Cancel drag if mouse leaves
                onClick={toggleChildZones}
                data-tooltip-id={`zip-tooltip-${zone.id}`}
                data-tooltip-content={renderZipCodes()}
              >
                {zone.zip_code.length > 100
                  ? `${zone.zip_code.substring(0, 100)}`
                  : zone.zip_code}
              </div>
              <Tooltip id={`zip-tooltip-${zone.id}`} place="top" className="custom-tooltip" />

              <div className="status">
                <div className="action">
                  <input
                    type="checkbox"
                    checked={zone.is_active}
                    onChange={() => handleStatusChange(zone.id, zone.is_active)}
                    id={`zone-status-${zone.id}`}
                  />
                  <label htmlFor={`zone-status-${zone.id}`}></label>
                </div>
              </div>

              <div className="action">
                <div
                  className="edit"
                  style={{ position: "absolute" }}
                  onClick={() => setSelectedZone((prevSelected) => (prevSelected === zone.id ? null : zone.id))}
                  onMouseLeave={handleEditMouseLeave}
                  onMouseEnter={handleEditMouseEnter}
                >
                  &#8942;
                  {selectedZone === zone.id && (
                    <div className="dropdown-menu">
                      <ul>
                        <li onClick={() => openZoneModal(zone)}>Edit Zone</li>
                        <li className="danger"
                          onClick={() => handleDelete(zone.id)}
                        >
                          Delete
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {isOpen && zone.children && (
              <div className="list-zone show">
                {zone.children.map((child) => (
                  <div key={child.id} className="list">
                    <div className="name">{child.name}</div>
                    <div className="zip-code">{child.zip_code}</div>
                    <div className="status">
                      <div className="action">
                        <input
                          type="checkbox"
                          checked={child.is_active}
                          onChange={() => handleStatusChange(child.id, child.is_active)}
                          id={`zone-status-${child.id}`}
                        />
                        <label htmlFor={`zone-status-${child.id}`}></label>
                      </div>
                    </div>
                    <div className="action">
                      <div
                        className="edit"
                        style={{ position: "absolute" }}
                        onClick={(e) => toggleEditDropdown(e, child.id)}
                        onMouseLeave={handleEditMouseLeave}
                        onMouseEnter={handleEditMouseEnter}
                      >
                        &#8942;
                        {editZoneId === child.id && (
                          <div className="dropdown-menu">
                            <ul>
                              <li onClick={() => openZoneModal(child)}>Edit Zone</li>
                              <li className="danger"
                                onClick={() => handleDelete(child.id)}
                              >
                                Delete
                              </li>
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </>
    );
  };
  const RenderUnavailableZone = ({ zone }) => {
    const [isOpen, setIsOpen] = useState(false);
    const isCommaSeparated = zone.zip_code.includes(',');
    const isSingleZip = !zone.zip_code.includes(',') && !zone.zip_code.includes('-');
    const zip_codeClass = isSingleZip ? "single" : isCommaSeparated ? "multi" : "range";
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const handleMouseUp = () => setIsDragging(false);
    const handleMouseLeave = () => setIsDragging(false);
    const timeoutRef = useRef(null);
    const [editZoneId, setEditZoneId] = useState(null);


    const renderZipCodes = () => {
      const zipCodes = zone.zip_code.split(',').map((zip) => zip.trim());
      return zipCodes.join(', '); // Concatenate with commas for inline display
    };

    const toggleChildZones = () => {
      setIsOpen((prevState) => !prevState);
    };

    const handleStatusChange = (id, currentStatus) => {
      toggleZoneStatus(id, currentStatus);
    };

    const handleMouseDown = (e) => {
      const container = e.currentTarget;
      setIsDragging(true);
      setStartX(e.pageX - container.offsetLeft); // Store initial mouse position
      setScrollLeft(container.scrollLeft); // Store initial scroll position
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return; // Exit if not dragging
      e.preventDefault();
      const container = e.currentTarget;
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX); // Distance moved
      container.scrollLeft = scrollLeft - walk; // Update scroll position
    };


    const handleEditMouseLeave = () => {
      timeoutRef.current = setTimeout(() => {
        setSelectedZone(null);
        setEditZoneId(null)
      }, 250);
    };

    const handleEditMouseEnter = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };

    // Toggles the edit dropdown for a specific zone
    const toggleEditDropdown = (e, zoneId) => {
      setEditZoneId((prevId) => (prevId === zoneId ? null : zoneId));
    };

    const handleDelete = async (zoneId) => {
      try {
        const response = await UnavailableZoneService.delete(zoneId, api);
        fetchUnavailableZones();
      } catch (error) {

      }
    }

    return (
      <>
        <div className="list-details">
          <div className="lists">
            <div className="header-list">
              <div className="name">{zone.email}</div>
              <div
                className={`zip-code ${isOpen ? "open" : ""} ${zip_codeClass}`}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave} // Cancel drag if mouse leaves
                onClick={toggleChildZones}
                data-tooltip-id={`zip-tooltip-${zone.id}`}
                data-tooltip-content={renderZipCodes()}
              >
                {zone.zip_code.length > 100
                  ? `${zone.zip_code.substring(0, 100)}`
                  : zone.zip_code}
              </div>
              <Tooltip id={`zip-tooltip-${zone.id}`} place="top" className="custom-tooltip" />

              {/* <div className="status">
                <div className="action">
                  <input
                    type="checkbox"
                    checked={zone.is_active}
                    onChange={() => handleStatusChange(zone.id, zone.is_active)}
                    id={`zone-status-${zone.id}`}
                  />
                  <label htmlFor={`zone-status-${zone.id}`}></label>
                </div>
              </div> */}

              <div className="action">
                <div
                  className="edit"
                  style={{ position: "absolute" }}
                  onClick={() => setSelectedZone((prevSelected) => (prevSelected === zone.id ? null : zone.id))}
                  onMouseLeave={handleEditMouseLeave}
                  onMouseEnter={handleEditMouseEnter}
                >
                  &#8942;
                  {selectedZone === zone.id && (
                    <div className="dropdown-menu">
                      <ul>
                        {/* <li onClick={() => openZoneModal(zone)}>Edit Zone</li> */}
                        <li className="danger"
                          onClick={() => handleDelete(zone.id)}
                        >
                          Delete
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {isOpen && zone.children && (
              <div className="list-zone show">
                {zone.children.map((child) => (
                  <div key={child.id} className="list">
                    <div className="name"></div>
                    <div className="zip-code">{child.zip_code} {child.name}</div>
                    <div className="status">
                      <div className="action">
                        <input
                          type="checkbox"
                          checked={child.is_active}
                          onChange={() => handleStatusChange(child.id, child.is_active)}
                          id={`zone-status-${child.id}`}
                        />
                        <label htmlFor={`zone-status-${child.id}`}></label>
                      </div>
                    </div>
                    <div className="action">
                      <div
                        className="edit"
                        style={{ position: "absolute" }}
                        onClick={(e) => toggleEditDropdown(e, child.id)}
                        onMouseLeave={handleEditMouseLeave}
                        onMouseEnter={handleEditMouseEnter}
                      >
                        &#8942;
                        {editZoneId === child.id && (
                          <div className="dropdown-menu">
                            <ul>
                              <li onClick={() => openZoneModal(child)}>Edit Zone</li>
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </>
    );
  };
  const toggleZoneStatus = async (id, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await ZoneService.update(id, { is_active: newStatus }, api);
      fetchZones(); // Refetch to get the updated zone list
    } catch (error) {
    }
  };

  const openZoneModal = (zone) => {
    setNewZone({
      id: zone.id,
      name: zone.name,
      zip_code: zone.zip_code,
      is_active: zone.is_active,
    });
    setIsOpen(true);
  };

  function redirectToTab(tabName) {
    navigate(`/management?tab=${tabName}`);

    if (tabName === "profile") setTabIndex(0);
    else if (tabName === "zone-management") setTabIndex(1);
    else if (tabName === "hotel-availability") setTabIndex(1);
    else if (tabName === "general-settings") setTabIndex(2);
    else setTabIndex(0);

  }

  return (
    <>
      <MenuHeader />
      <div className="bloc-management">
        <div className="container">
          <Tabs selectedIndex={tabIndex} onSelect={(index) => setTabIndex(index)}>
            <TabList>
              <Tab onClick={() => redirectToTab("profile")}>{t('tabs.myProfile')}</Tab>

              {!auth?.roles?.some(role => role.name === 'ROLE_HOTEL_MANAGER') && ['zones-manage', 'zones-read'].every(perm => auth?.permissions?.includes(perm)) && (
                <Tab onClick={() => redirectToTab("zone-management")}>{t('tabs.zoneManagement')}</Tab>
              )}
              {!auth?.roles?.some(role => role.name === 'ROLE_HOTEL_MANAGER') && ['unavailable_zones-manage', 'unavailable_zones-read'].every(perm => auth?.permissions?.includes(perm)) && (
                <Tab onClick={() => redirectToTab("unavailable-zones")}>{t('tabs.unavailableZones')}</Tab>
              )}
              {/* {auth?.roles?.some(role => role.name === 'ROLE_HOTEL_MANAGER') && (
                <Tab onClick={() => redirectToTab("hotel-availability")}>{t('tabs.hotelAvailability')}</Tab>
              )} */}
              {!auth?.roles?.some(role => role.name === 'ROLE_HOTEL_MANAGER') && ['general_settings-manage', 'general_settings-read'].every(perm => auth?.permissions?.includes(perm)) && (
                <Tab onClick={() => redirectToTab("general-settings")}>{t('tabs.generalSettings')}</Tab>
              )}

            </TabList>

            <TabPanel>
              <div className="bloc-search">
                <div className="container">
                  <div className="search">
                    <button className="menu-mobile-tabs" onClick={handleClick}></button>
                    <h2>{t('profile.title')}</h2>
                  </div>
                </div>
                <div className="profile">
                  <div className="account-name">
                    {userData.firstname} {userData.lastname}
                  </div>
                  <div className="account-avatar">
                    <span className="name">
                      {userData.firstname ? userData.firstname[0] : ''}
                      {userData.lastname ? userData.lastname[0] : ''}
                    </span>
                  </div>
                </div>
              </div>
              <form onSubmit={updateProfile}>
                <h2 className="left-align">{t('profile.personalInfo')}</h2>
                <div className="profile-form-inscription">
                  <div className="form-row">
                    <div className="form-left">
                      <label>{t('profile.labels.firstName')} *</label>
                      <div className="form-text">
                        <input
                          type="text"
                          name="firstname"
                          value={userData.firstname || ""}
                          onChange={handleInputChange}
                          disabled={!isEditingProfile}
                          required
                        />
                      </div>
                      <label>{t('profile.labels.email')} *</label>
                      <div className="form-text">
                        <input
                          type="email"
                          name="email"
                          value={userData.email || ""}
                          onChange={handleInputChange}
                          disabled={!isEditingProfile}
                          required
                        />
                      </div>
                    </div>
                    <div className="form-right">
                      <label>{t('profile.labels.lastName')} *</label>
                      <div className="form-text">
                        <input
                          type="text"
                          name="lastname"
                          value={userData.lastname || ""}
                          onChange={handleInputChange}
                          disabled={!isEditingProfile}
                          required
                        />
                      </div>
                      <label>{t('profile.labels.phone')} *</label>
                      <div className="form-text">
                        <input
                          type="text"
                          name="phone"
                          value={userData.phone || ""}
                          onChange={handleInputChange}
                          disabled={!isEditingProfile}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-actions" style={{ display: "flex", gap: "10px" }}>
                    {isEditingProfile ? (
                      <>
                        <button type="button" className="btn btn-danger" onClick={handleCancelEdit}>
                          {t('profile.buttons.cancel')}
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={!isFormModified()}
                        >
                          {t('profile.buttons.confirm')}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleEditProfileClick}
                      >
                        {t('profile.buttons.editProfile')}
                      </button>
                    )}
                  </div>
                </div>
              </form>
              <form onSubmit={handlePasswordSubmit}>
                <h2 className="left-align">{t('profile.password')}</h2>
                <div className="profile-form-inscription">
                  {!isEditingPassword ? (
                    <>
                      <label className="margeTop20">{t('profile.labels.currentPassword')} *</label>
                      <div className="form-text pass-input-size password-container">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="currentPassword"
                          className="password"
                          value={userData.currentPassword}
                          onChange={handleInputChange}
                          placeholder={t('profile.placeholders.password')}
                          required
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label="toggle password visibility"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </button>
                      </div>
                      <button type="button" onClick={handleEditPasswordClick} className="btn btn-primary">
                        {t('profile.buttons.editPassword')}
                      </button>
                    </>
                  ) : (
                    <>
                      <label className="margeTop20">{t('profile.labels.currentPassword')} *</label>
                      <div className="form-text pass-input-size password-container">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="currentPassword"
                          className="password"
                          value={userData.currentPassword}
                          onChange={handleInputChange}
                          placeholder={t('profile.placeholders.password')}
                          required
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label="toggle password visibility"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </button>
                      </div>
                      <label className="margeTop20">{t('profile.labels.newPassword')} *</label>
                      <div className="form-text pass-input-size password-container">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="newPassword"
                          className="password"
                          value={userData.newPassword}
                          onChange={handleInputChange}
                          placeholder={t('profile.placeholders.newPassword')}
                          required
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label="toggle password visibility"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </button>
                      </div>
                      <label className="margeTop20">{t('profile.labels.confirmPassword')} *</label>
                      <div className="form-text pass-input-size password-container">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="confirmPassword"
                          className="password"
                          value={userData.confirmPassword}
                          onChange={handleInputChange}
                          placeholder={t('profile.placeholders.confirmPassword')}
                          required
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label="toggle password visibility"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </button>
                      </div>
                      <button type="submit" className="btn btn-primary">
                        {t('profile.buttons.confirm')}
                      </button>
                    </>
                  )}
                </div>
              </form>
            </TabPanel>

            {!auth?.roles?.some(role => role.name === 'ROLE_HOTEL_MANAGER') && (
              <TabPanel>
                <div className="bloc-search">
                  <div className="container">
                    <div className="search">
                      <button className="menu-mobile-tabs" onClick={handleClick}></button>
                      <h2>{t('zoneManagement.title')}</h2>
                    </div>
                    <button onClick={openModal} className="btn-add-client">
                      {t('zoneManagement.buttons.add')}
                    </button>
                  </div>
                </div>
                <div className="list-management">
                  <div className="header-list">
                    <div className="name">{t('zoneManagement.labels.zoneName')}</div>
                    <div className="zip-code">{t('zoneManagement.labels.zipCode')}</div>
                    <div className="status">{t('zoneManagement.labels.status')}</div>
                    <div className="action">{t('zoneManagement.labels.action')}</div>
                  </div>
                  <div className="list-details">
                    {zones.length > 0 ? (
                      zones.map((zone) => (
                        <RenderZone key={zone.id} zone={zone} />
                      ))
                    ) : (
                      <div className="text-center">{t('zoneManagement.noZones')}</div>
                    )}
                  </div>
                </div>
              </TabPanel>
            )}
            {!auth?.roles?.some(role => role.name === 'ROLE_HOTEL_MANAGER') && (
              <TabPanel>
                <div className="bloc-search">
                  <div className="container">
                    <div className="search">
                      <button className="menu-mobile-tabs" onClick={handleClick}></button>
                      <h2>{t('unavailableZones.title')}</h2>
                    </div>
                  </div>
                </div>
                <div className="list-management">
                  <div className="header-list">
                    <div className="name">{t('unavailableZones.labels.userEmail')}</div>
                    <div className="zip-code">{t('unavailableZones.labels.zipCode')}</div>
                    <div className="action"></div>
                  </div>
                  <div className="list-details">
                    {unavailablezones.length > 0 ? (
                      unavailablezones.map((zone) => (
                        <RenderUnavailableZone key={zone.id} zone={zone} />
                      ))
                    ) : (
                      <div className="text-center">{t('zoneManagement.noZones')}</div>
                    )}
                  </div>
                </div>
              </TabPanel>
            )}
            {auth?.roles?.some(role => role.name === 'ROLE_HOTEL_MANAGER') && (
              <TabPanel>
                <div className="bloc-search">
                  <div className="container">
                    <div className="search">
                      <button className="menu-mobile-tabs" onClick={handleClick}></button>
                      <h2>{t('hotelAvailability.title')}</h2>
                    </div>
                  </div>
                </div>

                <div className="week-buttons btn-role-manager">
                  {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((day, index) => (
                    <button key={index} onClick={() => openAvailabilityModal(index)} className={`btn-submit ${selectedDay === index ? 'active' : ''}`}>
                      {day}
                    </button>
                  ))}
                </div>

                {availabilityModalOpen && (
                  <div className="open-disponibility">
                    <button className="btn-close" onClick={closeAvailabilityModal} style={{ float: 'right' }}>Fermer</button>

                    {/* New Availability Inputs */}
                    <div className="form-inscription form-hotel">
                      <div className="time-availiblity-container">
                        <div className="time-availiblity-input">
                          <label>{t('hotelAvailability.startTime')} *:</label>
                          <div className="form-text">
                            <input
                              type="time"
                              value={startTime}
                              onChange={(e) => setStartTime(e.target.value)}
                              required
                            />
                          </div>
                        </div>
                        <div className="time-availiblity-input">
                          <label>{t('hotelAvailability.endTime')} *:</label>
                          <div className="form-text">
                            <input
                              type="time"
                              value={endTime}
                              onChange={(e) => setEndTime(e.target.value)}
                              className="form-control"
                              required
                            />
                          </div>
                        </div>
                      </div>

                      <div style={{ marginTop: "20px", textAlign: "center" }}>
                        <button
                          onClick={addAvailability}
                          className="btn btn-success"
                          style={{ padding: "10px 20px", fontSize: "16px" }}
                        >
                          {editIndex !== null ? t('hotelAvailability.update') : t('hotelAvailability.create')}
                        </button>
                      </div>
                    </div>
                    <h2>{t('hotelAvailability.availabilityFor')} {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][selectedDay]}</h2>
                    <div className="tableau-list no-scroll">
                      <div className="container">
                        <div className="t-tableau">
                          <div className="t-header">
                            <div className="t-heure-debut">{t('hotelAvailability.startTime')}</div>
                            <div className="t-heure-fin">{t('hotelAvailability.endTime')}</div>
                            <div className="t-actions">{t('hotelAvailability.actions')}</div>
                          </div>
                          <div className="t-body">
                            {availabilities[selectedDay]?.length > 0 ? (
                              availabilities[selectedDay].map((availability, index) => (
                                console.log(availability),
                                console.log('Rendering availabilities for day', selectedDay, availabilities[selectedDay]),
                                <div className="t-list" key={index}>
                                  <div className="t-heure-debut">{availability.start_time}</div>
                                  <div className="t-heure-fin">{availability.end_time}</div>
                                  <div className="t-actions">
                                    <div className="actions-availibility">
                                      <button
                                        className="btn btn-warning btn-sm actions-availibility-button-size"
                                        onClick={() => editAvailability(index)}
                                      >
                                        {t('hotelAvailability.edit')}
                                      </button>
                                      <button
                                        className="btn btn-danger btn-sm actions-availibility-button-size"
                                        onClick={() => deleteAvailability(index)}
                                      >
                                        {t('hotelAvailability.delete')}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div>
                                <div colSpan="3" className="text-center">{t('hotelAvailability.noAvailability')}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </TabPanel>
            )}
            {!auth?.roles?.some(role => role.name === 'ROLE_HOTEL_MANAGER') && (
              <TabPanel>
                <div className="bloc-search">
                  <div className="container">
                    <div className="search">
                      <button className="menu-mobile-tabs" onClick={handleClick}></button>
                      <h2>{t('generalSettings.title')}</h2>
                    </div>
                  </div>
                </div>
                <form onSubmit={updateSettings}>
                  <div className="form-inscription form-general-settings">
                    {generalSettingData.length && generalSettingData.map(setting => {
                      return (<div className="form-text" key={setting.id}>
                        <h2 className="left-align">{t(`generalSettings.${setting.setting}`)}</h2>
                        <div className="time-editor">
                          <button type="button" id={`decrement-${setting.id}`} onClick={handleTimeDecrement} disabled={setting.value === 0}>-</button>

                          {/* {setting.setting === 'VTA' ? (
                            <>
                              <input
                                type="float"
                                value={setting.value}
                                onChange={(e) => handleTimeChange(setting.id, e.target.value)}
                                min="0"
                              /> <span className="time">%</span>
                            </>
                          ) : ( */}
                          <span className="time">{setting.setting === 'VTA' ? Number(setting.value).toFixed(2) : setting.value} {setting.setting === 'surcharge' ? t('generalSettings.surchargeUnit') : setting.setting === 'VTA' ? '%' : t('generalSettings.timeUnit')}</span>
                          {/* )} */}
                          <button type="button" id={`increment-${setting.id}`} onClick={handleTimeIncrement}>+</button>
                        </div>
                      </div>)
                    }
                    )}

                    <div className="form-actions" style={{ display: "flex", gap: "10px" }}>
                      {generalSettingData.find(setting => setting.updated) ? (
                        <>
                          <button type="button" className="btn btn-danger" onClick={handleCancelSettingsEdit}>
                            {t('generalSettings.buttons.cancel')}
                          </button>
                          <button
                            type="submit"
                            className="btn btn-primary"
                          >
                            {t('generalSettings.buttons.confirm')}
                          </button>
                        </>
                      ) : (<></>)}
                    </div>
                  </div>
                </form>
              </TabPanel>
            )}
          </Tabs>
        </div>
      </div>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={customStyles}
        contentLabel="Create Zone Modal"
      >
        <h2>{newZone.id ? t('zoneManagement.editZone') : t('zoneManagement.createZone')}</h2>
        <form onSubmit={createZone}>
          <div className="form-inscription">
            <div className="form-text">
              <label>{t('zoneManagement.labels.zoneName')} *</label>
              <input
                type="text"
                name="name"
                value={newZone.name}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-text">
              <label>{t('zoneManagement.labels.zipCode')} *</label>
              <input
                type="text"
                name="zip_code"
                value={newZone.zip_code}
                onChange={handleInputChange}
                required
                placeholder={t('zoneManagement.placeholders.zipCode')}
              />
              <br /><br />
              <label className="help-text">
                {t('zoneManagement.helpText')}
              </label>
            </div>
            <div className="actions">
              <button type="button" onClick={closeModal} className="btn btn-secondary ml-2">
                {t('zoneManagement.buttons.cancel')}
              </button>
              <button type="submit" className="btn btn-primary">
                {newZone.id ? t('zoneManagement.buttons.update') : t('zoneManagement.buttons.create')}
              </button>
            </div>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default Management;
